import React, { useEffect, useMemo, useState } from 'react';
import i18n from '../i18n';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../utils/dateFormat';


const card = {
    background: '#fff', borderRadius: 14, padding: 24, marginBottom: 20,
    border: '1px solid var(--sp-gray-100)'
};

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });
}

export default function AdminEmailSettingsPage() {
    const { t } = useTranslation(['adminEmailSettings', 'common', 'nav', 'messages', 'errors']);
    const PRESETS = useMemo(() => ({
        gmail: { label: 'Gmail', smtpHost: 'smtp.gmail.com', smtpPort: 587, useSsl: false, useStartTls: true, hint: t('adminEmailSettings:gmailHint') },
        yandex465: { label: t('adminEmailSettings:yandex465'), smtpHost: 'smtp.yandex.ru', smtpPort: 465, useSsl: true, useStartTls: false, hint: t('adminEmailSettings:yandex465Hint') },
        yandex587: { label: t('adminEmailSettings:yandex587'), smtpHost: 'smtp.yandex.ru', smtpPort: 587, useSsl: false, useStartTls: true, hint: t('adminEmailSettings:yandex587Hint') },
        mailru: { label: 'Mail.ru', smtpHost: 'smtp.mail.ru', smtpPort: 465, useSsl: true, useStartTls: false, hint: t('adminEmailSettings:mailruHint') },
        mailgun: { label: 'Mailgun', smtpHost: 'smtp.mailgun.org', smtpPort: 587, useSsl: false, useStartTls: true, hint: t('adminEmailSettings:mailgunHint') },
        outlook: { label: 'Outlook', smtpHost: 'smtp.office365.com', smtpPort: 587, useSsl: false, useStartTls: true, hint: null },
        custom: { label: t('adminEmailSettings:customSmtp'), smtpHost: '', smtpPort: 587, useSsl: false, useStartTls: true, hint: null }
    }), [t]);
    const testReadyLabel = t('adminEmailSettings:testReady');
    const role = localStorage.getItem('role') || '';
    const [provider, setProvider] = useState('custom');
    const [form, setForm] = useState({
        smtpHost: '',
        smtpPort: 465,
        encryption: 'ssl',
        senderName: i18n.t('adminEmailSettings:defaultSender'),
        senderEmail: '',
        username: '',
        password: '',
        adminNotificationEmail: '',
        isActive: false
    });
    const [hasPassword, setHasPassword] = useState(false);
    const [editPassword, setEditPassword] = useState(false);
    const [showPwd, setShowPwd] = useState(false);
    const [lastTest, setLastTest] = useState({ at: null, result: null });
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [msg, setMsg] = useState('');

    useEffect(() => {
        if (role !== 'Admin') return;
        load();
    }, [role]);

    function encryptionFromFlags(useSsl, useStartTls) {
        if (useSsl && !useStartTls) return 'ssl';
        if (useStartTls) return 'starttls';
        return 'none';
    }

    function flagsFromEncryption(enc) {
        if (enc === 'ssl') return { useSsl: true, useStartTls: false, smtpPort: 465 };
        if (enc === 'starttls') return { useSsl: false, useStartTls: true, smtpPort: 587 };
        return { useSsl: false, useStartTls: false, smtpPort: form.smtpPort || 25 };
    }

    async function load() {
        try {
            const res = await adminApi().get('/admin/email-settings');
            const d = res.data;
            const enc = encryptionFromFlags(d.useSsl, d.useStartTls);
            setForm({
                smtpHost: d.smtpHost || '',
                smtpPort: d.smtpPort || 465,
                encryption: enc,
                senderName: d.senderName || t('adminEmailSettings:defaultSender'),
                senderEmail: d.senderEmail || '',
                username: d.username || '',
                password: '',
                adminNotificationEmail: d.adminNotificationEmail || '',
                isActive: d.isActive
            });
            setHasPassword(d.hasPassword);
            setEditPassword(!d.hasPassword);
            setLastTest({ at: d.lastTestedAt, result: d.lastTestResult });
        } catch {
            setMsg(t('adminEmailSettings:msgLoadError'));
        }
    }

    function applyPreset(key) {
        setProvider(key);
        const p = PRESETS[key];
        const enc = encryptionFromFlags(p.useSsl, p.useStartTls);
        setForm(f => ({
            ...f,
            smtpHost: p.smtpHost,
            smtpPort: p.smtpPort,
            encryption: enc,
            username: f.senderEmail || f.username
        }));
        setTestResult(null);
    }

    function applyRecommended(rec) {
        if (!rec) return;
        const enc = rec.useSsl && !rec.useStartTls ? 'ssl' : (rec.useStartTls ? 'starttls' : 'none');
        setForm(f => ({
            ...f,
            smtpHost: rec.smtpHost,
            smtpPort: rec.smtpPort,
            encryption: enc
        }));
        setMsg(t('adminEmailSettings:msgAppliedTest'));
    }

    function setEncryption(enc) {
        const flags = flagsFromEncryption(enc);
        setForm(f => ({
            ...f,
            encryption: enc,
            smtpPort: flags.smtpPort,
            useSsl: flags.useSsl,
            useStartTls: flags.useStartTls
        }));
    }

    async function save(e) {
        e.preventDefault();
        setSaving(true);
        setMsg('');
        setTestResult(null);
        const flags = flagsFromEncryption(form.encryption);
        try {
            const body = {
                smtpHost: form.smtpHost,
                smtpPort: form.smtpPort,
                useSsl: flags.useSsl,
                useStartTls: flags.useStartTls,
                senderEmail: form.senderEmail,
                senderName: form.senderName,
                username: form.username,
                adminNotificationEmail: form.adminNotificationEmail || null,
                isActive: form.isActive
            };
            if (editPassword && form.password) body.password = form.password;
            await adminApi().put('/admin/email-settings', body);
            setMsg(t('adminEmailSettings:msgSaved'));
            setEditPassword(false);
            setForm(f => ({ ...f, password: '' }));
            load();
        } catch (err) {
            setMsg(err.response?.data?.message || t('adminEmailSettings:msgSaveError'));
        }
        setSaving(false);
    }

    async function testConnection() {
        setTesting(true);
        setTestResult(null);
        setMsg('');
        const flags = flagsFromEncryption(form.encryption);
        if (!form.username?.trim()) {
            setTestResult({
                ok: false,
                text: t('adminEmailSettings:msgEnterUsername')
            });
            setTesting(false);
            return;
        }
        const body = {
            smtpHost: form.smtpHost,
            smtpPort: form.smtpPort,
            useSsl: flags.useSsl,
            useStartTls: flags.useStartTls,
            senderEmail: form.senderEmail,
            senderName: form.senderName,
            username: form.username,
            adminNotificationEmail: form.adminNotificationEmail || null
        };
        if (form.password) body.password = form.password;
        else if (!hasPassword) {
            setTestResult({
                ok: false,
                text: t('adminEmailSettings:msgEnterAppPassword')
            });
            setTesting(false);
            return;
        }
        try {
            const res = await adminApi().post('/admin/email-settings/test', body);
            if (res.data.recommended) applyRecommended(res.data.recommended);
            setTestResult({
                ok: true,
                text: res.data.message,
                profile: res.data.profileUsed
            });
            load();
        } catch (err) {
            const d = err.response?.data;
            const status = err.response?.status;
            // Раньше при любом неожиданном ответе (не наш JSON с message — например,
            // 500 от ASP.NET Core с Problem Details, обрыв сети, таймаут) текст падал в
            // общий "Ошибка подключения" без единой полезной детали. Теперь всегда
            // показываем то, что реально есть: код статуса, title/detail из Problem
            // Details, либо текст сетевой ошибки — вместо немой заглушки.
            const fallback = status === 401
                ? t('adminEmailSettings:msgSessionExpired', {
                    defaultValue: 'Сессия администратора истекла или недействительна (HTTP 401) — это ответ нашего API, до проверки SMTP дело не дошло. Выйдите и войдите снова под Admin.'
                })
                : status
                    ? `HTTP ${status}: ${d?.title || d?.detail || err.message || t('adminEmailSettings:msgConnectionError')}`
                    : (err.message || t('adminEmailSettings:msgConnectionError'));
            setTestResult({
                ok: false,
                text: d?.message || fallback,
                hint: d?.hint
            });
        }
        setTesting(false);
    }

    const presetHint = PRESETS[provider]?.hint;

    if (role !== 'Admin') {
        return (
            <OwnerPageLayout title={t('adminEmailSettings:title')}>
                <p>{t('adminEmailSettings:adminOnly')}</p>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout
            title={t('adminEmailSettings:title')}
            subtitle={t('adminEmailSettings:subtitle')}
        >
            <div style={{
                ...card,
                background: '#fff8f8',
                borderColor: '#fecaca'
            }}>
                <strong>{t('adminEmailSettings:menuHint')}</strong> {t('adminEmailSettings:menuPath')}
                {' '}{t('adminEmailSettings:menuAlt')}{' '}
                <a href="/admin/email-settings" style={{ color: '#EB3333' }}>/admin/email-settings</a>
            </div>
            <form onSubmit={save}>
                <div style={card}>
                    <h3 style={{ marginTop: 0 }}>{t('adminEmailSettings:smtpProvider')}</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                        {Object.entries(PRESETS).map(([key, p]) => (
                            <button
                                key={key}
                                type="button"
                                onClick={() => applyPreset(key)}
                                style={{
                                    padding: '12px 18px', borderRadius: 12, cursor: 'pointer', fontWeight: 600,
                                    border: provider === key ? '2px solid #EB3333' : '1.5px solid var(--sp-gray-200)',
                                    background: provider === key ? '#fff5f5' : '#fff'
                                }}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    {presetHint && (
                        <p style={{ fontSize: 13, color: '#64748b', marginTop: 12, marginBottom: 0 }}>{presetHint}</p>
                    )}
                    {(provider === 'yandex465' || provider === 'yandex587') && (
                        <p style={{ fontSize: 12, color: '#0d47a1', marginTop: 10, marginBottom: 0, lineHeight: 1.5 }}>
                            {t('adminEmailSettings:yandexTestHint')}
                        </p>
                    )}
                </div>

                <div style={card}>
                    <p style={{ fontSize: 13, color: '#64748b', marginTop: 0, lineHeight: 1.5 }}>
                        {t('adminEmailSettings:encryptionHelp')}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 16, marginBottom: 16, marginTop: 16 }}>
                        <div>
                            <label style={{ fontSize: 13, fontWeight: 600 }}>{t('adminEmailSettings:smtpServer')}</label>
                            <input
                                className="sp-input"
                                value={form.smtpHost}
                                onChange={e => setForm(f => ({ ...f, smtpHost: e.target.value }))}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 13, fontWeight: 600 }}>{t('adminEmailSettings:port')}</label>
                            <input
                                className="sp-input"
                                type="number"
                                value={form.smtpPort}
                                onChange={e => {
                                    const port = Number(e.target.value);
                                    // Порт и режим шифрования раньше могли разъехаться (ручной ввод
                                    // порта не трогал переключатель SSL/STARTTLS) — 465 без SSL или
                                    // 587 без STARTTLS почти всегда ошибка пользователя, а не
                                    // осознанный выбор, поэтому подстраиваем шифрование под порт
                                    // так же, как это делает бэкенд (NormalizeEncryptionFlags).
                                    setForm(f => ({
                                        ...f,
                                        smtpPort: port,
                                        encryption: port === 465 ? 'ssl' : port === 587 ? 'starttls' : f.encryption
                                    }));
                                }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    </div>

                    <fieldset style={{ border: 'none', padding: 0, marginBottom: 16 }}>
                        <legend style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>{t('adminEmailSettings:encryption')}</legend>
                        {[
                            { id: 'ssl', label: 'SSL' },
                            { id: 'starttls', label: 'STARTTLS' },
                            { id: 'none', label: t('adminEmailSettings:encNone') }
                        ].map(opt => (
                            <label key={opt.id} style={{ marginRight: 20, cursor: 'pointer' }}>
                                <input
                                    type="radio"
                                    name="encryption"
                                    checked={form.encryption === opt.id}
                                    onChange={() => setEncryption(opt.id)}
                                />
                                {' '}{opt.label}
                            </label>
                        ))}
                    </fieldset>

                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 600 }}>{t('adminEmailSettings:senderName')}</label>
                        <input
                            className="sp-input"
                            value={form.senderName}
                            onChange={e => setForm(f => ({ ...f, senderName: e.target.value }))}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 600 }}>{t('adminEmailSettings:senderEmail')}</label>
                        <input
                            className="sp-input"
                            type="email"
                            value={form.senderEmail}
                            onChange={e => setForm(f => ({ ...f, senderEmail: e.target.value }))}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 600 }}>{t('adminEmailSettings:username')}</label>
                        <input
                            className="sp-input"
                            value={form.username}
                            onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                            placeholder={form.senderEmail || t('adminEmailSettings:usernamePlaceholder')}
                            style={{ width: '100%' }}
                        />
                        <p style={{ fontSize: 12, color: '#64748b', margin: '6px 0 0' }}>
                            {t('adminEmailSettings:yandexUsernameHint')}
                        </p>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 600 }}>{t('adminEmailSettings:password')}</label>
                        {hasPassword && !editPassword ? (
                            <>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                                    <input className="sp-input" disabled placeholder="••••••••" style={{ flex: 1 }} />
                                    <button type="button" className="sp-btn sp-btn--secondary" onClick={() => setEditPassword(true)}>
                                        {t('adminEmailSettings:changePassword')}
                                    </button>
                                </div>
                                <p style={{ fontSize: 12, color: '#b45309', margin: '6px 0 0' }}>
                                    {t('adminEmailSettings:savedPasswordNote', {
                                        defaultValue: 'Проверка и отправка используют уже СОХРАНЁННЫЙ пароль. Чтобы проверить новый пароль приложения, нажмите «Изменить пароль» и введите его заново.'
                                    })}
                                </p>
                            </>
                        ) : (
                            <div style={{ display: 'flex', gap: 8 }}>
                                <input
                                    className="sp-input"
                                    type={showPwd ? 'text' : 'password'}
                                    value={form.password}
                                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                                    placeholder={hasPassword ? t('adminEmailSettings:newPassword') : ''}
                                    style={{ flex: 1 }}
                                />
                                <button type="button" onClick={() => setShowPwd(s => !s)} style={{ padding: '0 12px' }}>
                                    {showPwd ? '🙈' : '👁'}
                                </button>
                            </div>
                        )}
                    </div>
                    <div style={{ marginBottom: 16 }}>
                        <label style={{ fontSize: 13, fontWeight: 600 }}>{t('adminEmailSettings:adminNotifyEmail')}</label>
                        <input
                            className="sp-input"
                            type="email"
                            value={form.adminNotificationEmail}
                            onChange={e => setForm(f => ({ ...f, adminNotificationEmail: e.target.value }))}
                            style={{ width: '100%' }}
                        />
                        <p style={{ fontSize: 12, color: '#64748b', margin: '6px 0 0' }}>
                            {t('adminEmailSettings:adminNotifyHint')}
                        </p>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={form.isActive}
                            onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                        />
                        {t('adminEmailSettings:activateSending')}
                    </label>
                </div>

                {lastTest.at && (
                    <div style={{
                        ...card,
                        background: (lastTest.result || '').startsWith(testReadyLabel) ? '#e8f5e9' : '#ffebee'
                    }}>
                        {(lastTest.result || '').startsWith(testReadyLabel) ? (
                            <span>{t('adminEmailSettings:lastTestOk', { date: formatDateTime(lastTest.at) })}</span>
                        ) : (
                            <span>{t('adminEmailSettings:lastTestFail', { date: formatDateTime(lastTest.at), error: lastTest.result })}</span>
                        )}
                    </div>
                )}

                {testResult && (
                    <div style={{
                        ...card,
                        background: testResult.ok ? '#e8f5e9' : '#ffebee',
                        color: testResult.ok ? '#1b5e20' : '#b71c1c'
                    }}>
                        {testResult.ok ? (
                            <>
                                <div>✓ {testResult.text}</div>
                                {testResult.profile && (
                                    <div style={{ marginTop: 8, fontSize: 13, opacity: 0.9 }}>
                                        {t('adminEmailSettings:profile', { profile: testResult.profile })}
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                <div>✗ {testResult.text}</div>
                                {testResult.hint && <div style={{ marginTop: 8, fontSize: 13 }}>{testResult.hint}</div>}
                            </>
                        )}
                    </div>
                )}

                {msg && <p style={{ color: msg === t('adminEmailSettings:msgSaved') ? '#2e7d32' : '#c62828' }}>{msg}</p>}

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <button type="submit" className="sp-btn sp-btn--primary" disabled={saving}>
                        {saving ? t('adminEmailSettings:saving') : t('common:save')}
                    </button>
                    <button
                        type="button"
                        className="sp-btn sp-btn--secondary"
                        disabled={testing}
                        onClick={testConnection}
                    >
                        {testing ? t('adminEmailSettings:connecting') : t('adminEmailSettings:testConnection')}
                    </button>
                </div>
            </form>
        </OwnerPageLayout>
    );
}
